import { get } from 'lodash';
import { CONFIGURATOR_MODE, MENU_GROUPS } from './constants';
import { getSteps } from './steps';

export default {
  isItemChecked(state) {
    return (field, value) => {
      const { form } = state;
      return get(form, field) === value;
    };
  },
  currentStepState(state) {
    const { currentStep } = state;

    if (currentStep.id) {
      return state.stepsState[currentStep.id] || {};
    }

    return null;
  },
  getStepState(state) {
    return stepId => state.stepsState[stepId];
  },
  hasPreviousStep(state) {
    const { currentStep, stepsIds } = state;

    if (!currentStep.id) {
      return false;
    }

    return stepsIds.indexOf(currentStep.id) - 1 >= 0;
  },
  isFirstStep(state, getters) {
    const { stepNumber } = getters;
    return stepNumber === 1;
  },
  isLastStep(state, getters) {
    const { stepsState } = state;
    const { stepNumber } = getters;

    return Object.keys(stepsState).length === stepNumber;
  },
  stepNumber(state) {
    const { currentStep, stepsIds } = state;

    if (!stepsIds || !currentStep.id) {
      return null;
    }

    return stepsIds.indexOf(currentStep.id) + 1;
  },
  getMenuItems(state, getters) {
    if (!state.type) {
      return [];
    }

    const { creating, editing, completed, getStepState } = getters;
    const { currentStep } = state;

    const createNewMenuItem = () => ({
      label: null,
      active: false,
      current: false,
      number: null,
      groupId: null,
      children: [],
    });

    const steps = getSteps(state.type);
    const stepsIds = Array.from(steps.keys());
    const menuItems = [];

    steps.forEach((step, stepId) => {
      if (step.visibleInMenu === false) {
        return;
      }

      const menuItem = {
        ...createNewMenuItem(),
        ...{
          stepId,
          label: step.label,
          childOf: step.childOf,
          childOfGroup: step.group,
        },
      };
      const stepState = getStepState(stepId) || {};

      const itemIndex = stepsIds.indexOf(stepId);
      const currentItemIndex = stepsIds.indexOf(currentStep.id);

      menuItem.active =
        currentItemIndex !== -1 &&
        itemIndex < currentItemIndex &&
        stepState.valid$;
      menuItem.current = stepId === currentStep.id;

      if (editing) {
        menuItem.active = stepState.valid$;
      }

      if (creating && completed) {
        menuItem.active = true;
      }

      menuItems.push(menuItem);
    });

    // Create menu groups
    MENU_GROUPS.forEach(group => {
      if (!group.id || !group.afterStep) {
        return;
      }

      const index = menuItems.findIndex(x => x.stepId === group.afterStep);

      if (index === -1) {
        return;
      }

      const groupMenuItem = createNewMenuItem();
      groupMenuItem.label = group.label;
      groupMenuItem.groupId = group.id;

      menuItems.splice(index + 1, 0, groupMenuItem);
    });

    // Group menu items
    return menuItems
      .map(item => {
        if (item.childOf) {
          const parentItem = menuItems.find(x => x.stepId === item.childOf);

          if (parentItem) {
            parentItem.children.push(item);
            return null;
          }
        }

        if (item.childOfGroup) {
          const parentItem = menuItems.find(
            x => x.groupId === item.childOfGroup,
          );

          if (parentItem) {
            parentItem.children.push(item);
            return null;
          }
        }

        return item;
      })
      .filter(x => x !== null)
      .map((item, index) => {
        const stepNumber = index + 1;

        if (item.groupId && item.children.length > 0) {
          item.active = item.children.every(child => child.active);
        }

        if (item.children && item.children.length > 0) {
          item.children = item.children.map((childItem, childIndex) => {
            return {
              ...childItem,
              stepNumber: `${stepNumber}.${childIndex + 1}`,
            };
          });
        }

        return { ...item, stepNumber };
      });
  },
  getStepNumber(state, getters) {
    const { getMenuItems: menuItems } = getters;

    const findById = (items, stepId) => {
      let result = null;

      items.forEach(item => {
        if (result) {
          return;
        }
        if (item.stepId === stepId) {
          result = item;
        } else if (Array.isArray(item.children)) {
          result = findById(item.children, stepId);
        }
      });
      return result;
    };

    return stepId => {
      const menuItem = findById(menuItems, stepId);
      return menuItem ? menuItem.stepNumber : null;
    };
  },
  editing(state) {
    return state.mode === CONFIGURATOR_MODE.EDIT;
  },
  creating(state) {
    return state.mode === CONFIGURATOR_MODE.CREATE;
  },
  completed(state) {
    return state.currentStep.id === 'summary';
  },
  isFormValid(state) {
    const { stepsState } = state;

    if (!stepsState) {
      return false;
    }

    return Object.entries(stepsState).every(([, stepState]) => {
      return stepState.valid$ && !stepState.loading$;
    });
  },
  hasConfigurations(state) {
    const { configurations = [] } = state;
    return configurations.length > 0;
  },
};
